import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { ConsentType } from '../../consent/model/consent-checkbox';
import { ConsentProvider } from '../../consent/services/consent.provider';
import { DealerConsentVisibilityService } from '../../consent/services/dealer-consent-visibility.service';
import { DealerConsentService } from '../../consent/services/dealer-consent.service';
import { GcdmLanguageProvider } from '../../consent/services/gcdm-language.provider';
import { RetailCrmService } from '../../customer/clients/retail-crm/retail-crm.service';
import { Customer } from '../../customer/model/customer.model';
import { FoundCustomer, FoundRetailCustomer } from '../../customer/model/found-customer.model';
import { SearchService } from '../../customer/services/search.service';
import { useI18n } from '../../i18n/services/i18n.service';
import { usePagination } from '../../utils/pagination/pagination.provider';
import { ConsentsFormData } from '../model/consents-form-data';
import { CustomerCreationOutcome } from '../model/customer-creation-outcome';
import { CustomerForCreation } from '../model/customer-for-creation';
import { CustomerCreationService } from '../services/customer-creation.service';
import { CustomerConsentsFormContainer } from './customer-consents-form/customer-consents-form.container';
import { CustomerCreationFormContainer } from './customer-creation-form/customer-creation-form.container';

@Component({
  selector: 'cdf-customer-creation-modal',
  providers: [ConsentProvider, GcdmLanguageProvider],
  imports: [CustomerCreationFormContainer, CustomerConsentsFormContainer],
  template: `
    @switch (pagination.currentName()) {
      @case ('creation') {
        <cdf-customer-creation-form
          [initialCustomerInfo]="initialCustomerInfo()"
          [(saving)]="saving"
          [(duplicates)]="duplicates"
          (closeModal)="closeModal.emit()"
          (saveCustomer)="saveCustomer($event)"
          (openCustomerProfile)="openCustomerProfile.emit($event)"
        />
      }
      @case ('nscConsents') {
        <cdf-customer-consents-form
          [saving]="saving()"
          [customerLanguage]="createdCustomer()?.correspondenceLanguage"
          [bmwMiniId]="createdCustomer()?.privateContact?.email"
          [consentType]="ConsentType.NSC"
          (skip)="skip()"
          (saveAndSend)="saveNscConsentsFormData($event)"
        />
      }
      @case ('dealerConsents') {
        <cdf-customer-consents-form
          [saving]="saving()"
          [customerLanguage]="createdCustomer()?.correspondenceLanguage"
          [bmwMiniId]="createdCustomer()?.privateContact?.email"
          [consentType]="ConsentType.DEALER"
          (skip)="skip()"
          (saveAndSend)="saveDealerConsents($event)"
        />
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerCreationModalContainer {
  private readonly searchService = inject(SearchService);
  private readonly dealerConsentVisibilityService = inject(DealerConsentVisibilityService);
  private readonly dealerConsentService = inject(DealerConsentService);
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly customerCreationService = inject(CustomerCreationService);
  private readonly consentProvider = inject(ConsentProvider);
  private readonly dsToastService = inject(DsToastService);
  private readonly logger = inject(NGXLogger);
  protected readonly pagination = usePagination(this.getPages(), () => {
    this.complete(this.createdCustomer());
  });

  private readonly toastId = 'CUSTOMER_CREATION_MODAL_CONTAINER_TOAST';

  protected readonly i18n = useI18n();
  readonly closeModal = output();
  readonly initialCustomerInfo = input<Customer>();
  readonly openCustomerProfile = output<FoundRetailCustomer>();

  protected readonly saving = signal(false);
  protected readonly createdCustomer = signal<Customer | undefined>(undefined);
  readonly duplicates = signal<FoundCustomer[] | undefined>(undefined);

  private getPages() {
    return this.dealerConsentVisibilityService.showDealerConsent()
      ? (['creation', 'nscConsents', 'dealerConsents'] as const)
      : (['creation', 'nscConsents'] as const);
  }

  private complete(customer: Customer | undefined) {
    if (!customer) {
      this.logger.warn(
        '#CDFTF0074 Unable to complete customer completion process as the provided accountNumber is undefined.',
      );
      this.showOpenCustomerProfileError();
      return;
    }
    this.openCustomerProfile.emit(this.newCustomerToFoundCustomer(customer));
  }

  private newCustomerToFoundCustomer(customer: Customer): FoundRetailCustomer {
    return {
      gcid: customer.gcid,
      accountNumber: customer.accountNumber,
      bmwMiniId: customer.bmwMiniId,
      firstName: customer.firstName,
      middleName: customer.middleName,
      lastName: customer.lastName,
      suffix: customer.suffix,
      address: customer.privateContact.address,
      email: customer.privateContact.email ?? customer.businessContact.email,
      phone: customer.privateContact.phone ?? customer.businessContact.phone,
      mobile: customer.privateContact.mobile ?? customer.businessContact.mobile,
      isIndividual: true, // All new customers created with CDF are individuals
      customerToVehicleRelations: new Set(), // a new customer has no C2Vs
      taxNumber: customer.taxNumber,
      nationalId1: customer.nationalId1,
      nationalId1DocumentType: customer.nationalId1DocumentType,
      inSyncWithDirectSales: customer.inSyncWithDirectSales,
    };
  }

  protected skip() {
    this.pagination.next();
  }

  protected async saveCustomer(customer: CustomerForCreation) {
    // "saving" is intentionally not set to "true", as this is already done in customer-information-dialog.container
    try {
      this.dsToastService.popToast(this.toastId);

      if (!customer.ignoreDuplicate) {
        const duplicates = await this.searchForDuplicates(customer);
        if (duplicates) {
          this.duplicates.set(duplicates);
          return;
        }
      }

      const outcome = await this.createCustomer(customer);

      if (outcome.status === 'NSC_LINK_FAILED') {
        // The customer exists in the NSC Database but has no Retail CRM account, so there is no
        // profile to open and no accountNumber to hang the consent steps on. Stay in the modal
        // and tell the user, instead of pretending the creation failed altogether.
        this.logger.error(
          `#CDFTF0275 Customer was created in the NSC Database under GCID ${outcome.gcid}, ` +
            `but could not be imported into Retail CRM.`,
        );
        this.showNscDatabaseLinkFailed();
        return;
      }

      const createdCustomer = await this.retailCrmService.getAccountByAccountNumber(outcome.accountNumber);
      this.createdCustomer.set(createdCustomer);

      if (customer.syncToNscDatabase) {
        // The GCID is assigned synchronously by GCDM, so the consent pages can be reached
        // immediately - no waiting for a delayed assignment (CUSTAPP-611).
        this.pagination.next();
      } else if (!this.pagination.goTo('dealerConsents')) {
        // A Retail-CRM-only customer has no GCID, so the NSC consent page is skipped. If the market
        // has no dealer consents either, there is no step left: finish the flow by opening the
        // profile, instead of leaving the user on the creation form without feedback (CUSTAPP-775).
        this.complete(createdCustomer);
      }
    } catch (error: unknown) {
      this.logger.warn('#CDFTF0061 Failed saving customer', error);
      this.showCustomerCreationError();
    } finally {
      this.saving.set(false);
    }
  }

  private async searchForDuplicates(customer: CustomerForCreation): Promise<FoundCustomer[] | undefined> {
    try {
      const duplicateCustomers = await this.searchService.searchCustomers({
        email: customer.customer.privateContact.email,
      });

      // Return undefined if no duplicate customers were found
      return duplicateCustomers?.length ? duplicateCustomers : undefined;
    } catch (error: unknown) {
      this.logger.warn('#CDFTF00029 An error occurred when checking for duplicates:', error);
      return;
    }
  }

  private async createCustomer(customer: CustomerForCreation): Promise<CustomerCreationOutcome> {
    try {
      return await this.customerCreationService.createCustomer(customer);
    } catch (error: unknown) {
      throw new Error(`#CDFTF0028 An error occurred when creating a customer:`, { cause: error });
    }
  }

  private showNscDatabaseLinkFailed() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('customerCreation.nscDatabaseLinkFailedTitle'),
      toastText: this.i18n('customerCreation.nscDatabaseLinkFailedMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  protected saveDealerConsents(formData: ConsentsFormData) {
    const consentSelection = formData.consentValues;
    const customer = this.createdCustomer();
    this.saving.set(true);

    if (!customer) {
      this.logger.error('#CDFTF0112 Unable to save dealer consents as no customer was saved yet.');
      this.showConsentSaveError();
      return;
    }

    const dealerConsent = this.dealerConsentService.applyDealerConsentSelection(
      customer.dealerConsent,
      consentSelection,
    );
    this.dealerConsentService
      .saveDealerConsent(customer, dealerConsent)
      .then(() => this.pagination.next())
      .catch((e) => {
        this.showConsentSaveError();
        this.logger.warn('#CDFTF0111 Could not save dealer consents', e);
      })
      .finally(() => this.saving.set(false));
  }

  protected async saveNscConsentsFormData(formData: ConsentsFormData) {
    this.dsToastService.popToast(this.toastId);
    this.saving.set(true);
    try {
      const gcid = this.createdCustomer()?.gcid;
      if (!gcid) {
        throw new Error('#CDFTF0073 Unable to save consents and preregister BMW/MINI ID as no gcid is present.');
      }

      try {
        await this.consentProvider.confirmConsentSelection(gcid, formData.consentValues, true);
      } catch (e: unknown) {
        this.logger.error('#CDFTF0045 Error when saving consents', e);
        this.showConsentError(formData);

        // If consents could not be saved, we do not advance to the next page.
        // The user has to try again.
        return;
      }

      try {
        // preregister will throw an error if the GCID/email is blank.
        // If brandedIdType is none, the function is a no-op.
        await this.consentProvider.preregister(
          gcid,
          this.createdCustomer()?.privateContact.email || '',
          formData.brandedIdType,
        );
      } catch (e: unknown) {
        this.logger.error('#CDFTF0057 Preregistration failed', e);
        this.showPreregistrationErrorAfterSavingConsents();
      } finally {
        // If consents were saved but preregistration failed, we still advance to the next page.
        // The user can complete the preregistration on the profile page
        this.pagination.next();
      }
    } finally {
      this.saving.set(false);
    }
  }

  private showCustomerCreationError() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('customerCreation.errorTitle'),
      toastText: this.i18n('customerCreation.error'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showConsentError(formData: ConsentsFormData) {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('consent.errorTitle'),
      toastText: this.i18n.enum(formData.brandedIdType, 'consent.error'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showConsentSaveError() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('consent.errorTitle'),
      toastText: this.i18n('consent.saveError'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showPreregistrationErrorAfterSavingConsents() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('preregistration.errorTitle'),
      toastText: this.i18n('preregistration.errorAfterConsents'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showOpenCustomerProfileError() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('searchResults.openProfileErrorTitle'),
      toastText: this.i18n('searchResults.openProfileErrorMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  protected readonly ConsentType = ConsentType;
}
